import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from "../components/ui/empty"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

export function EmptyRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()

  // Compound mode: children are empty-header, empty-content, etc.
  // Slot renderers (registered in index.ts) handle each child type.
  const hasCompoundChildren = element.children?.some(
    (c) => c.type === "empty-header" || c.type === "empty-content"
  )

  if (hasCompoundChildren) {
    return (
      <Empty className={cn(stripPresetConflicts(element.className))} data-testid={`empty-${element.id}`}>
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Empty>
    )
  }

  // Legacy mode: title/description/icon as props
  const title = element.text ? String(resolveSymbol(element.text) ?? "") : ""
  const description = element.description ? String(resolveSymbol(element.description) ?? "") : ""
  const hasIcon = !!element.icon

  const mediaChildren = element.children?.filter(
    (c) => c.type === "avatar-group" || c.type === "avatar" || c.type === "image"
  ) ?? []
  const contentChildren = element.children?.filter(
    (c) => c.type !== "avatar-group" && c.type !== "avatar" && c.type !== "image"
  ) ?? []

  return (
    <Empty className={cn(stripPresetConflicts(element.className))} data-testid={`empty-${element.id}`}>
      <EmptyHeader>
        {hasIcon && element.icon !== "avatar-group" && (
          <EmptyMedia variant="icon">
            <ElementRenderer element={{ id: `${element.id}-icon`, type: "icon", icon: element.icon }} />
          </EmptyMedia>
        )}
        {mediaChildren.length > 0 && (
          <EmptyMedia variant="default">
            {mediaChildren.map((child) => <ElementRenderer key={child.id} element={child} />)}
          </EmptyMedia>
        )}
        {title && <EmptyTitle>{title}</EmptyTitle>}
        {description && <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>
      {contentChildren.length > 0 && (
        <EmptyContent>
          {contentChildren.map((child) => <ElementRenderer key={child.id} element={child} />)}
        </EmptyContent>
      )}
    </Empty>
  )
}
